import { useEffect } from "react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { toast } from "sonner";
import { Modal } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import withPermission from "../../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import { getLevelTypeByID, updateLevelType } from "../../../api/settings";

type UUID = string;
type Props = {
  levelTypeID: UUID;
  show: boolean;
  onClose: () => void;
  onSaved: () => void;
};

const EditLevelTypeModal = ({ levelTypeID, show, onClose, onSaved }: Props) => {
  const createSchema = z.object({
    label: z.string().nonempty("Libellé requis"),
  });

  type FormFields = z.infer<typeof createSchema>;
  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
  });

  //Traitement après validation du formulaire de création d'. année académique
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      //Envoi des datas pour modification vers le backend
      await updateLevelType(data, levelTypeID);

      reset(); //On réinitialise les champs du formulaire
      if (onSaved) onSaved(); // On fait un reload de la liste

      toast.success("Type de niveau mise à jour !"); //Toast de succès

      onClose(); // On ferme le modal
    } catch (error: any) {
      // Si erreur
      // console.log(error);
      setError("root", {
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      });
    }
  };

  useEffect(() => {
    if (!show || !levelTypeID) return; // ✅ Ne rien faire si le modal n'est pas ouvert

    const fetchLevelType = async () => {
      try {
        const response = await getLevelTypeByID(levelTypeID);
        if (!response) {
          // Si aucune année trouvé
          throw new Error("Type de niveau introuvable");
        }

        reset({
          // Si année trouvé on rempli les champs du formulaire
          label: response.data.label,
        });
      } catch (error) {
        onClose(); // On ferme le modal
        // console.log(error)
      }
    };

    fetchLevelType();
  }, [show, levelTypeID, reset]);

  return (
    <>
      <Modal show={show} onHide={() => onClose()}>
        <Modal.Header closeButton>
          <Modal.Title>Modifier un type de niveau </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form
            id="academic-year-form"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row row-cols-xxl-5 row-cols-md-6">
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Libellé
                  </label>
                  <input
                    {...register("label")}
                    type="text"
                    className={
                      errors.label ? "form-control is-invalid" : "form-control"
                    }
                    placeholder="Libellé de l'année"
                  />
                  {errors.label && (
                    <div className="invalid-feedback">
                      {errors.label.message}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Annuler
          </button>

          {isSubmitting ? (
            <button
              className="btn btn-primary-light"
              type="button"
              disabled={false}
            >
              <span
                className="spinner-border spinner-border-sm align-middle me-1"
                role="status"
                aria-hidden="true"
              />
              Chargement...
            </button>
          ) : (
            <button
              type="submit"
              form="academic-year-form" // 🔗 liaison au formulaire
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              Enregistrer
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withPermission(
  EditLevelTypeModal,
  [buildPermission(Resource.ACADEMIC_YEAR, Action.UPDATE)],
  "masquer",
);
